// src/padplanner/EditorPage.jsx
//
// /padplanner/plans/$planId/edit (S4b), mockup 4: the plan editor. The left
// panel holds the palette (Templates), the Layers tab and the Checks tab; the
// map holds the FootprintEditor (ui/src/lib/footprint-editor), a toolbar
// along the top, and the Selected panel at the top right.
//
// ── WHO GETS IT ─────────────────────────────────────────────────────────────
//
// GET /plans/:id answers can_edit: can_edit_site_plans, and the plan neither
// archived nor on a frozen revision. The plan page links here only when it is
// true. A read-only viewer who types the URL gets a sentence and a link back,
// and no editor: nothing here is useful to someone who cannot save.
//
// ── SAVING (editorSave.js) ──────────────────────────────────────────────────
//
// A debounced autosave plus Save, batch PUT against the token. "Saved <time>"
// is the response's plan.updated_at. A 409 revision_changed stops saving and
// says "This plan changed elsewhere": the user's unsaved work stays on the
// map until they reload, with no silent overwrite and no merge. Any other
// refusal is shown with the server's code. The first save of a revision
// with no local_epsg answers the one the server chose, and the editor adopts
// it (applySaved) before any more math.
//
// ── THE PALETTE (editorPalette.js) ──────────────────────────────────────────
//
// The pinned set plus any published standard set. An unpinned plan shows the
// standard sets and offers Choose library: a published set of the org's own,
// pinned once and for good.
//
// ── CHECKS (S5b) ────────────────────────────────────────────────────────────
//
// The server's run is the record. It arrives with every save (`checks`: the
// run, and only its fail and no_subjects results) and from GET …/checks (every
// result), which the Checks tab loads when it opens on a run it has not read
// in full. ChecksTab.jsx draws the tab and the Layout checks panel;
// ChecksLayers.js the witness lines, their "84′ · min 100′" labels and the
// redzone halos, each behind a layer toggle on the Layers tab. While dragging,
// clearanceMirror.js measures the dragged features against the published
// rules in the revision's frame; the next save's results replace that.
//
// Clicking a Checks row selects its features and frames them: the one camera
// move here besides the first load, passed as USER_ACTION so PadPlannerMap
// counts it as the user's (THE CAMERA in PadPlannerMap.jsx).
//
// ── IMAGERY ─────────────────────────────────────────────────────────────────
//
// The editor draws what the plan page opens on, over the Mapbox basemap and
// under the features: PadPlannerMap's raster, its URL from api/tileUrl.js.
// The capture is chosen by usePlanImagery.js, the plan page's own code: the
// pin (revision.imagery), or with none the newest collect, marked "Not
// pinned — showing newest capture (<date>)". Showing it is not pinning it.
// With nothing to show, Mapbox only, and the notice says why (DB, 2026-10-29).
//
// The editor draws the imagery strip too, and pins like the plan page (S-e
// slice 3, DB's answer 1; the editor opens only on an editable draft). A chip
// shows its capture; Pin and Unpin sit at the captures row's end. The pin is
// a revision write, and it moves the token the editor's feature saves are
// held to, so it goes through the saver (editorSave.js writeRevision): only
// while nothing is unsaved or in flight, with the saver's base, and the saver
// saves on from the token it answers. A pin refused as revision_changed is
// the editor's conflict (Reload), as a refused save is.
//
// ── ONE LAYOUT, TWO PAGES (S6a2) ────────────────────────────────────────────
//
// The FootprintEditor and its ChecksLayers are mounted through planLayout.js,
// the templates and rules loaded through usePlanLibrary.js, and the Layers tab
// is LayoutPanels.jsx's: the plan page draws a revision read-only with the
// same code, and PlanLayout.parity.test.jsx holds the two to the same drawing.
//
// Submit for review is in the head (PlanReview.jsx SubmitButton, the plan
// page's own), disabled while there are unsaved changes. A submitted revision
// is frozen: GET /plans/:id answers can_edit false, so the plan page hides
// Open editor and this page shows READ_ONLY_TEXT.
//
// ── CUT ─────────────────────────────────────────────────────────────────────
//
// Count and patterns, Service company, 3D mesh,
// the drone scan epoch, "1 in = 40 ft" (the editor's scale bar instead), text
// annotations, Group, and the measure, dimension, text and comment tools.
// Share and the avatars (S6b and later).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import PadPlannerGate from "./PadPlannerGate";
import PadPlannerMap from "./PadPlannerMap";
import SidePanel, { SectionLabel } from "./SidePanel";
import StatusPill from "./StatusPill";
import { TemplatePreview } from "./TemplatesTab";
import { utmZoneLabel } from "../lib/footprint-editor/frame.js";
import { footprintDims, zoneRadiusFt, ALIGN_MODES, lineLengthsFt, formatFeet } from "../lib/footprint-editor/geometry.js";
import { LINE_TYPES, LINE_TYPE_LABELS, DEFAULT_LINE_TYPE, lineTypeLabel, lineWidthFt } from "./lineVocabulary";
import FeatureSaver from "./editorSave";
import { paletteSets, placeable } from "./editorPalette";
import {
  getPlan, getFeatures, saveFeatures, getChecks, runChecks, getApproval, updateRevision,
} from "../api/padplannerApi";
import { CATEGORY_LABELS, groupTemplates, matchesSearch, setChipLabel } from "./templateVocabulary";
import { formatApi10, formatSavedAt, imageryLabel, imageryEmptyText, includeForCapture, referenceForCapture, creditForCapture } from "./planVocabulary";
import { usePlanLanes, openingImagery, notPinnedText, notPinnedEmptyText, notPinnedShowingText } from "./usePlanImagery";
import { ImageryStrip } from "../components/imagery/TimeStrip";
import { selectAndZoom, sitePlannerRows } from "./imageryStripRows";
import { refusalText } from "./planRefusals";
import ChecksTab, { LayoutChecksPanel } from "./ChecksTab";
import { checksTabLabel } from "./checksVocabulary";
import { liveReading, shapeCache } from "./clearanceMirror";
import { mountLayout, drawRedzones, frameResult } from "./planLayout";
import { usePlanTemplates, useRuleSets } from "./usePlanLibrary";
import { LayersTab } from "./LayoutPanels";
import { SubmitButton } from "./PlanReview";
import ReadFailed from "./ReadFailed";
import ChooseLibrary from "./ChooseLibrary";
import { useMapView, useEditorDrawingStyle } from "./mapView";
import { usePadAlign, axisText } from "./padAlign";
import { relativeDeg } from "../lib/footprint-editor/padAxis.js";
import { provisionalEpsg } from "../lib/footprint-editor/frame.js";
import { PADPLANNER_DISPLAY_NAME } from "../constants/analyticsProducts";

export const CONFLICT_TEXT = "This plan changed elsewhere — reload to see the current version.";
export const CONFLICT_KEEP_TEXT = "Your unsaved changes stay on the map until you reload. Nothing was overwritten.";
export const READ_ONLY_TEXT = "You can view this plan, but not edit it.";
export const NO_PERIMETER_TEXT = "This plan has no perimeter yet. Set it on the plan page before placing anything: it decides the frame the layout is measured in.";
export const NO_WELLS_TEXT = "This plan has no wells yet.";
export const PALETTE_FOOTER_TEXT = "Click a template, then the map · true-scale footprints · snap to pad edges";

const field = "w-full rounded border border-bg-border bg-bg px-2 py-1.5 text-[13px] text-fg-primary";
const tabClass = (on) =>
  `-mb-px border-b-2 px-3 py-1.5 text-[12.5px] uppercase tracking-wide ${on ? "border-accent-action text-fg-primary" : "border-transparent text-fg-secondary hover:text-fg-primary"}`;
const chipClass = (on) =>
  `rounded-full border px-2.5 py-1 text-[12px] ${on ? "border-accent-action bg-accent-action/15 text-fg-primary" : "border-bg-border text-fg-secondary hover:text-fg-primary"}`;
// Tool buttons say their mode with aria-pressed; the mockup outlines the active one.
const btn = "op-btn op-btn--sm whitespace-nowrap aria-pressed:border-accent-action aria-pressed:bg-accent-action/15 aria-pressed:text-fg-primary";

export default function EditorPage({ planId }) {
  return (
    <PadPlannerGate>
      <Editor planId={planId} />
    </PadPlannerGate>
  );
}

function Editor({ planId }) {
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState({ id: null, plan: null, canEdit: false, features: null, error: null });
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getPlan(planId), getFeatures(planId)])
      .then(([p, f]) => {
        if (cancelled) return;
        setLoaded({ id: planId, plan: p.plan, canEdit: p.can_edit === true, features: f, error: null });
        setPlan(p.plan);
      })
      .catch((e) => { if (!cancelled) setLoaded({ id: planId, plan: null, canEdit: false, features: null, error: e.status === 404 ? "Plan not found." : e.message }); });
    return () => { cancelled = true; };
  }, [planId]);

  const canEdit = loaded.id === planId && loaded.canEdit;
  // A read-only viewer gets no editor, so no library either.
  const { library, templates, templatesById, retry: retryLibrary } = usePlanTemplates(plan, canEdit);

  const current = loaded.id === planId ? loaded : null;
  if (current?.error) return <div className="p-6 text-sm text-fg-secondary" role="alert">{current.error}</div>;
  if (!current?.plan || !plan) return null;
  if (!current.canEdit) {
    return (
      <div className="flex flex-col items-start gap-3 p-6" data-testid="pp-editor-read-only">
        <p className="text-sm text-fg-primary">{READ_ONLY_TEXT}</p>
        <button type="button" className={btn} onClick={() => navigate({ to: "/padplanner/plans/$planId", params: { planId } })}>Back to the plan</button>
      </div>
    );
  }
  return (
    <EditorLoaded key={planId} plan={plan} setPlan={setPlan} initial={current.features} library={library} retryLibrary={retryLibrary}
      templates={templates} templatesById={templatesById}
      onNavigate={(to, params, search) => navigate({ to, params, ...(search ? { search } : {}) })} />
  );
}

function EditorLoaded({ plan, setPlan, initial, library, retryLibrary, templates, templatesById, onNavigate }) {
  const [map, setMap] = useState(null);
  const layoutRef = useRef(null);
  const editorRef = useRef(null);
  const saverRef = useRef(null);
  const [, setTick] = useState(0);
  const rerender = useCallback(() => setTick((t) => t + 1), []);
  const [saveState, setSaveState] = useState({ status: "saved", savedAt: plan.updated_at, error: null });
  const [mode, setMode] = useState("select");
  const [armed, setArmed] = useState(null);
  const [tab, setTab] = useState("templates");
  const [hidden, setHidden] = useState(() => new Set());
  const [reloadError, setReloadError] = useState(null);
  const checksRef = useRef(null);
  const [checks, setChecks] = useState({ loaded: false, error: null, run: null, rules: null, results: [], full: false });
  const [publishedId, setPublishedId] = useState(null);
  const [approval, setApproval] = useState(null);
  const [approvalError, setApprovalError] = useState(null);
  const [overlays, setOverlays] = useState({ layout: true, redzones: true });
  const [rerun, setRerun] = useState({ busy: false, error: null });
  // What the plan page would open on, chosen by the plan page's own code.
  const { lanes, error: lanesError, retry: retryLanes } = usePlanLanes(plan.id, plan.revision.perimeter);
  const pinned = plan.revision.imagery?.dataset_key ?? null;
  const imagery = openingImagery(pinned, lanes);
  // The chip picked, forgotten whenever the pin changes (as on the plan page); undefined: none.
  const [cursor, setCursor] = useState({ pin: pinned, key: undefined });
  const picked = cursor.pin === pinned ? cursor.key : undefined;
  const shownKey = picked !== undefined ? picked : imagery.key;
  const [pinState, setPinState] = useState({ busy: false, error: null });

  // The rules of the published set, and of the run's set when that differs
  // (it names each row's rule): fetched once each.
  const { ruleSets, failed: rulesFailed, retry: retryRules } = useRuleSets([publishedId, checks.run?.rule_set_id ?? null]);
  const rulesById = useMemo(() => new Map([...ruleSets.values()].flat().map((r) => [r.id, r])), [ruleSets]);
  const publishedRules = (publishedId && ruleSets.get(publishedId)) || null;
  // The editor's callbacks are bound once; these keep what they read current.
  const live = useRef({});
  live.current = { rules: publishedRules, templatesById };

  // A write's checks (a save's or a pin's answer): the run it made, its problems only.
  const adoptChecks = useCallback((response) => {
    if (!response?.checks?.run) return;
    const run = response.checks.run;
    checksRef.current?.setLive(null);
    setChecks((c) => ({ ...c, loaded: true, error: null, run, rules: run.rules ?? c.rules, results: response.checks.results ?? [], full: false }));
    if (run.rule_set_id) setPublishedId(run.rule_set_id);
  }, []);

  const onMapReady = useCallback((m) => {
    if (!m) {
      layoutRef.current?.destroy(); layoutRef.current = null;
      checksRef.current = null; editorRef.current = null;
    }
    setMap(m);
  }, []);

  // One editor per map; it outlives template and plan updates.
  useEffect(() => {
    if (!map) return undefined;
    const layout = mountLayout({
      map, localEpsg: initial.revision.local_epsg ?? plan.revision.local_epsg,
      perimeter: plan.revision.perimeter, templates, features: initial.features, keyboard: true,
      scaleBar: null,   // MapLibre's, in the map's bottomRight slot (MapHud.jsx), as on the plan page
      onChange: ({ reason }) => { if (reason !== "load") saverRef.current?.changed(); rerender(); },
      onSelectionChange: rerender,
      onModeChange: (m2) => { setMode(m2); if (m2 === "select") setArmed(null); },
      onPadAxisChange: (c) => setPadChoiceRef.current(c),
      // While dragging: the nearest violation or near-limit pair, in the frame.
      // When the drag ends the reading stays until the save's results arrive.
      onPreview: (moved) => {
        const layers = checksRef.current;
        if (!layers) return;
        if (!moved) { drag = null; return; }
        const { rules, templatesById: byId } = live.current;
        const templateOf = (f) => (f.template_id ? byId.get(f.template_id) ?? null : null);
        if (!drag || drag.frame !== editor.frame) drag = { frame: editor.frame, shapeOf: shapeCache(templateOf, editor.frame) };
        layers.setLive(liveReading({ moved, features: editor.getFeatures(), templateOf, frame: editor.frame, rules, shapeOf: drag.shapeOf }));
      },
    });
    const { editor } = layout;
    let drag = null;
    layoutRef.current = layout;
    editorRef.current = editor;
    checksRef.current = layout.checks;
    const saver = new FeatureSaver({
      planId: plan.id, base: initial.token, save: saveFeatures,
      getPayload: () => editor.toPayload(),
      onSaved: (response, sent) => {
        // local_epsg first: applySaved adopts it before redrawing anything.
        editor.applySaved({ sent, response });
        if (response.plan) setPlan(response.plan);
        adoptChecks(response);
      },
      onState: setSaveState,
    });
    saverRef.current = saver;
    rerender();
    return () => {
      saver.stop();
      layout.destroy();
      if (layoutRef.current === layout) { layoutRef.current = null; editorRef.current = null; checksRef.current = null; }
    };
    // The editor is made once per map; later data reaches it through setTemplates / setPerimeter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => { editorRef.current?.setTemplates(templates); }, [templates, map]);

  // Align to pad (padAlign.js): this user's choice on this plan, a view-and-snap aid only.
  const [padChoice, setPadChoice] = usePadAlign(plan.id);
  const setPadChoiceRef = useRef(setPadChoice);
  setPadChoiceRef.current = setPadChoice;
  useEffect(() => { editorRef.current?.setPadAxis(padChoice); rerender(); }, [padChoice, map]);   // eslint-disable-line react-hooks/exhaustive-deps

  // The checks: every result on load, and again whenever the tab opens on a
  // run it has only the problems of (a save's answer).
  const runId = checks.run?.id ?? null;
  const wantFull = !checks.loaded || (tab === "checks" && !checks.full && runId !== null);
  useEffect(() => {
    if (!wantFull) return undefined;
    let cancelled = false;
    getChecks(plan.id)
      .then((r) => {
        if (cancelled) return;
        // A save may have answered a newer run while this read was out.
        setChecks((c) => (c.run && r.run && c.run.id !== r.run.id && Date.parse(c.run.run_at) > Date.parse(r.run.run_at) ? c
          : { loaded: true, error: null, run: r.run, rules: r.rules, results: r.results ?? [], full: true }));
        setPublishedId(r.rule_set?.id ?? null);
      })
      .catch((e) => { if (!cancelled) setChecks((c) => ({ ...c, loaded: true, error: refusalText(e) })); });
    return () => { cancelled = true; };
  }, [plan.id, wantFull, runId]);

  // What Submit needs besides the verdict: whether this user may, and the route.
  const loadApproval = useCallback(() => getApproval(plan.id)
    .then((a) => { setApproval(a); setApprovalError(null); })
    .catch((e) => { setApproval(null); setApprovalError(e); }), [plan.id]);
  useEffect(() => { loadApproval(); }, [loadApproval]);

  useEffect(() => { checksRef.current?.setResults(checks.results); }, [checks.results, map]);
  useEffect(() => { checksRef.current?.setVisible(overlays); }, [overlays, map]);
  // The halos follow the wells: recomputed after each change the page renders.
  useEffect(() => { drawRedzones(layoutRef.current, { rules: publishedRules, templatesById }); });

  async function rerunChecks() {
    setRerun({ busy: true, error: null });
    try {
      const r = await runChecks(plan.id);
      const run = r.checks.run;
      setChecks({ loaded: true, error: null, run, rules: run.rules, results: r.checks.results ?? [], full: true });
      if (run.rule_set_id) setPublishedId(run.rule_set_id);
      setRerun({ busy: false, error: null });
    } catch (err) { setRerun({ busy: false, error: err }); }
  }

  /** A Checks row: select the features it names and frame them (with the witness). The user asked. */
  function pickResult(r) {
    frameResult(layoutRef.current, map, r);
    rerender();
  }

  // Leaving with unsaved work asks first.
  useEffect(() => {
    const warn = (e) => { if (saverRef.current?.dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  const editor = editorRef.current;
  // Imagery | Basemap | Paper (mapView.js). Paper's grid is drawn in the editor's own frame at its snap step,
  // so the dots are where a snapped anchor lands.
  const mapView = useMapView();
  useEditorDrawingStyle(editor, mapView, checksRef.current);
  const gridEpsg = editor?.frame?.epsg ?? plan.revision.local_epsg ?? provisionalEpsg(plan.revision.perimeter);

  async function reload() {
    if (saverRef.current?.dirty && !window.confirm("Reload the plan? Your unsaved changes on this page will be discarded.")) return;
    setReloadError(null);
    try {
      const [p, f] = await Promise.all([getPlan(plan.id), getFeatures(plan.id)]);
      setPlan(p.plan);
      const ed = editorRef.current;
      if (ed) {
        ed.setPerimeter(p.plan.revision.perimeter);
        ed.load(f.features, { localEpsg: f.revision.local_epsg });
      }
      saverRef.current?.reset(f.token, p.plan.updated_at);
    } catch (err) { setReloadError(refusalText(err)); }
  }

  /** Pin (key) or unpin (null), through the saver: held to its base, and it saves on from the answer's token. */
  async function pinImagery(key) {
    const saver = saverRef.current;
    if (!saver?.idle || pinState.busy) return;
    setPinState({ busy: true, error: null });
    try {
      const r = await saver.writeRevision((base) => updateRevision(plan.id, { imagery: key ? { dataset_key: key } : null, base }));
      if (r?.plan) setPlan(r.plan);
      adoptChecks(r);
      setPinState({ busy: false, error: null });
    } catch (err) { setPinState({ busy: false, error: refusalText(err) }); }
  }

  function arm(t) {
    if (!editor) return;
    if (armed === t.id) { editor.cancel(); return; }
    editor.startPlace({ template_id: t.id });
    setArmed(t.id);
  }

  const selected = editor?.selectedFeatures() ?? [];
  // The strip: Pin for a collect shown and not pinned, Unpin for a pin; both
  // wait while a change is unsaved or out (the pin moves the save's token).
  const pinBlocked = pinState.busy || saveState.status !== "saved";
  const pinTitle = saveState.status === "conflict" ? "The plan changed elsewhere. Reload first." : pinBlocked ? "Waiting for the save." : undefined;
  const shownIsCollect = Boolean(lanes?.collects?.some((c) => c.dataset_key === shownKey));
  const pinActions = (
    <>
      {shownKey && shownKey !== pinned && shownIsCollect && (
        <button type="button" className="op-btn op-btn--primary op-btn--sm" disabled={pinBlocked} title={pinTitle} onClick={() => pinImagery(shownKey)}>Pin</button>
      )}
      {pinned && <button type="button" className="op-btn op-btn--sm" disabled={pinBlocked} title={pinTitle} onClick={() => pinImagery(null)}>Unpin</button>}
    </>
  );
  const stripRows = lanes ? sitePlannerRows({ lanes, cursorKey: shownKey, markKey: pinned, emptyText: imageryEmptyText(lanes.imagery_entitled), end: pinActions }) : null;
  const shownLabel = stripRows?.flatMap((r) => r.chips).find((c) => c.key === shownKey)?.label ?? null;
  const hint = mode === "place" ? `Click the map to place ${templatesById.get(armed)?.name ?? "it"}. Esc cancels.`
    : mode === "line" ? LINE_HINT_TEXT
    : mode === "edge" ? EDGE_PICK_HINT_TEXT
    : null;

  return (
    <div className="flex h-full min-h-0">
      <SidePanel label="Plan editor" head={<EditorHead plan={plan} saveState={saveState} editor={editor}
        onSave={() => saverRef.current?.saveNow()} onNavigate={onNavigate}
        submit={<SubmitButton planId={plan.id} planNumber={plan.plan_number} approval={approval} checks={checks} canEdit
          approvalError={approvalError} onRetry={loadApproval} onRerun={rerunChecks} rerun={rerun}
          dirty={saveState.status !== "saved"}
          onSubmitted={() => onNavigate("/padplanner/plans/$planId", { planId: plan.id })} />} />}>
        <div className="flex border-b border-bg-border" role="tablist" aria-label="Editor">
          <button type="button" role="tab" aria-selected={tab === "templates"} className={tabClass(tab === "templates")} onClick={() => setTab("templates")}>Templates</button>
          <button type="button" role="tab" aria-selected={tab === "layers"} className={tabClass(tab === "layers")} onClick={() => setTab("layers")}>Layers</button>
          <button type="button" role="tab" aria-selected={tab === "checks"} className={tabClass(tab === "checks")} onClick={() => setTab("checks")}
            data-testid="pp-checks-tab">{checksTabLabel(checks)}</button>
        </div>
        {tab === "templates" && (
          <Palette plan={plan} library={library} onRetryLibrary={retryLibrary} armed={armed} onArm={arm} disabled={!editor?.frame}
            onPinned={(p) => setPlan(p)} />
        )}
        {tab === "layers" && (
          <LayersTab editor={editor} hidden={hidden} onToggle={(layer, visible) => {
            editor?.setLayerVisibility(layer, visible);
            setHidden((h) => { const n = new Set(h); if (visible) n.delete(layer); else n.add(layer); return n; });
          }} overlays={overlays} onOverlay={(patch) => setOverlays((o) => ({ ...o, ...patch }))} />
        )}
        {tab === "checks" && rulesFailed.length > 0 && <ReadFailed what="the clearance rules these checks name" onRetry={retryRules} testid="pp-rules-read-failed" />}
        {tab === "checks" && <ChecksTab checks={checks} rulesById={rulesById} onRerun={rerunChecks} rerun={rerun} onPick={pickResult} />}
      </SidePanel>

      <div className="relative min-w-0 flex-1" data-testid="pp-editor-map">
        <PadPlannerMap fitKey={plan.id} perimeter={plan.revision.perimeter} perimeterDashed rasterKey={shownKey}
          rasterCredit={creditForCapture({ pin: plan.revision.imagery, lanes, key: shownKey })}
          footprint={{ role: "editor", include: includeForCapture({ pin: plan.revision.imagery, lanes, key: shownKey }),
            withReference: referenceForCapture({ pin: plan.revision.imagery, lanes, key: shownKey }) }}
          onMapReady={onMapReady} hint={hint} showCursor scaleBar
          view={mapView} gridEpsg={gridEpsg} gridStepFt={editor?.getSnapping().gridFt ?? 5} gridAxis={editor?.getPadAxis() ?? null}
          readout={plan.revision.imagery ? [{ label: "Imagery", value: `Pinned: ${imageryLabel(plan.revision.imagery)}` }] : []}
          hud={{
            topCenter: [
              <EditorToolbar key="toolbar" editor={editor} mode={mode} selectedCount={selected.length} onChange={rerender}
                padChoice={padChoice} onPadChoice={setPadChoice} />,
              !plan.revision.perimeter && (
                <p key="no-perimeter" className="max-w-md rounded-md border border-bg-border bg-bg/95 px-3 py-2 text-[13px] text-fg-primary shadow" role="status">
                  {NO_PERIMETER_TEXT}
                </p>
              ),
              saveState.status === "conflict" && (
                <div key="conflict" className="flex max-w-lg flex-col gap-2 rounded-md border border-accent-red/60 bg-bg/95 px-3 py-2 shadow-lg"
                  role="alert" data-testid="pp-conflict">
                  <p className="text-[13px] font-semibold text-fg-primary">{CONFLICT_TEXT}</p>
                  <p className="op-meta">{CONFLICT_KEEP_TEXT}</p>
                  <div className="flex gap-2">
                    <button type="button" className="op-btn op-btn--primary op-btn--sm" onClick={reload}>Reload</button>
                  </div>
                  {reloadError && <p className="text-sm text-accent-red">{reloadError}</p>}
                </div>
              ),
              saveState.status === "error" && saveState.error && (
                <p key="save-error" className="max-w-lg rounded bg-bg/95 px-3 py-1.5 text-sm text-accent-red shadow" role="alert" data-testid="pp-save-error">
                  Not saved: {refusalText(saveState.error)} <span className="op-mono">({saveState.error.code ?? saveState.error.status})</span>
                </p>
              ),
            ],
            topRight: selected.length > 0 && editor && (
              <div key="selected" className="max-h-[45vh] w-[290px] max-w-full overflow-y-auto">
                <SelectedPanel editor={editor} features={selected} templatesById={templatesById} wells={plan.revision.wells}
                  onChange={rerender} onWells={() => onNavigate("/padplanner/plans/$planId", { planId: plan.id }, { tab: "wells" })} />
              </div>
            ),
            bottomLeft: overlays.layout && <LayoutChecksPanel key="checks" checks={checks} rulesById={rulesById} />,
            bottomCenter: [
              !imagery.pinned && lanes && (
                <p key="no-imagery" className="max-w-md rounded bg-bg/90 px-2.5 py-1 text-[12px] text-fg-secondary shadow"
                  role="status" data-testid="pp-editor-no-imagery">
                  {picked !== undefined && shownLabel ? notPinnedShowingText(shownLabel)
                    : imagery.key ? notPinnedText(imagery.capture) : notPinnedEmptyText(lanes.imagery_entitled)}
                </p>
              ),
              pinState.error && (
                <p key="pin-error" className="max-w-lg rounded bg-bg/95 px-3 py-1.5 text-sm text-accent-red shadow" role="alert" data-testid="pp-pin-error">
                  {pinState.error}
                </p>
              ),
              lanesError && (
                <ReadFailed key="lanes" what="the imagery dates" onRetry={retryLanes} testid="pp-lanes-failed"
                  className="rounded-md border border-bg-border bg-bg/95 px-3 py-1.5 shadow" />
              ),
            ],
          }}
          bottom={stripRows && ((strip) => (
            <ImageryStrip onChip={selectAndZoom((key) => setCursor({ pin: pinned, key }), strip)} rows={sitePlannerRows({ lanes, cursorKey: shownKey,
              markKey: pinned, emptyText: imageryEmptyText(lanes.imagery_entitled), end: pinActions, ...strip })} />
          ))} />
      </div>
    </div>
  );
}

/* ── head ──────────────────────────────────────────────────────────────── */

const SAVE_STATUS_TEXT = { saving: "Saving…", dirty: "Unsaved changes", conflict: "Not saved: changed elsewhere", error: "Not saved" };

function EditorHead({ plan, saveState, editor, onSave, onNavigate, submit }) {
  const saved = saveState.status === "saved" ? formatSavedAt(saveState.savedAt) : SAVE_STATUS_TEXT[saveState.status];
  return (
    <>
      <nav className="op-meta flex items-center gap-1" aria-label="Breadcrumb">
        <button type="button" className="hover:text-fg-primary" onClick={() => onNavigate("/padplanner")}>{PADPLANNER_DISPLAY_NAME}</button>
        <span aria-hidden="true">/</span>
        <button type="button" className="hover:text-fg-primary" onClick={() => onNavigate("/padplanner/plans/$planId", { planId: plan.id })}>
          <span className="op-mono">{plan.plan_number}</span>
        </button>
        <span aria-hidden="true">/</span>
        <span className="text-fg-primary">Editor</span>
      </nav>
      <div className="flex flex-col gap-1.5">
        <h1 className="font-display text-[19px] font-semibold leading-tight text-fg-primary">{plan.title}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={plan.status} archived={Boolean(plan.archived_at)} suffix={`v${plan.current_revision}`} />
          <span className="op-meta" data-testid="pp-save-status">{saved}</span>
          {editor?.frame && <span className="op-meta" data-testid="pp-frame">{editor.frame.label}</span>}
        </div>
        <div className="flex gap-2 pt-1">
          <button type="button" className="op-btn op-btn--primary op-btn--sm" onClick={onSave}
            disabled={saveState.status === "conflict" || saveState.status === "saving"}>Save</button>
          <button type="button" className={btn} onClick={() => onNavigate("/padplanner/plans/$planId", { planId: plan.id })}>Back to plan</button>
        </div>
        {submit}
      </div>
    </>
  );
}

/* ── the palette ───────────────────────────────────────────────────────── */

function Palette({ plan, library, onRetryLibrary, armed, onArm, disabled, onPinned }) {
  const { chips, choosable, canChoose } = useMemo(() => paletteSets(plan, library.sets ?? []), [plan, library.sets]);
  const [activeId, setActiveId] = useState(null);
  const [q, setQ] = useState("");
  const active = chips.find((c) => c.id === activeId) ?? chips[0] ?? null;
  const set = active ? library.bySet[active.id] : null;
  const shown = useMemo(() => placeable(set?.templates).filter((t) => matchesSearch(t, q)), [set, q]);
  const groups = useMemo(() => groupTemplates(shown), [shown]);

  return (
    <div className="flex flex-col gap-3" data-testid="pp-palette">
      {canChoose && <ChooseLibrary plan={plan} choosable={choosable} buttonClass={btn} onPinned={(p, id) => { setActiveId(id); onPinned(p); }} />}
      <input type="search" className={field} placeholder="Search templates…" aria-label="Search templates"
        value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Template sets">
        {chips.map((c) => (
          <button key={c.id} type="button" aria-pressed={active?.id === c.id} className={chipClass(active?.id === c.id)} onClick={() => setActiveId(c.id)}>
            {c.standard ? "AI PRIME standard" : setChipLabel(c)}
            {c.retired && <span className="ml-1.5 op-chip px-1 py-0">Retired</span>}
          </button>
        ))}
      </div>
      {library.failed && <ReadFailed what="the template library" onRetry={onRetryLibrary} testid="pp-palette-failed" />}
      {!library.failed && active && !set && <p className="op-meta">Loading templates…</p>}
      {set && shown.length === 0 && <p className="op-meta">{q.trim() ? `No templates match “${q.trim()}”.` : "This set has nothing to place."}</p>}
      {groups.map((g) => (
        <section key={g.key} className="flex flex-col gap-1.5" aria-labelledby={`pp-eg-${g.key}`}>
          <div className="flex items-center justify-between">
            <SectionLabel id={`pp-eg-${g.key}`}>{g.label}</SectionLabel>
            <span className="op-mono text-[11px] text-fg-secondary">{g.templates.length}</span>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {g.templates.map((t) => (
              <button key={t.id} type="button" aria-pressed={armed === t.id} data-testid="pp-palette-tile" disabled={disabled}
                onClick={() => onArm(t)}
                className={`flex min-h-[72px] flex-col items-center justify-center gap-1.5 rounded border px-1.5 py-2 text-center text-[11.5px] leading-tight disabled:opacity-50 ${armed === t.id ? "border-accent-action bg-accent-action/10 text-fg-primary" : "border-bg-border bg-bg text-fg-primary hover:border-fg-secondary"}`}>
                <TemplatePreview t={t} />
                <span>{t.name}</span>
              </button>
            ))}
          </div>
        </section>
      ))}
      <p className="op-meta border-t border-bg-border pt-2">{PALETTE_FOOTER_TEXT}</p>
    </div>
  );
}

/* ── the toolbar ───────────────────────────────────────────────────────── */

const ALIGN_LABELS = { left: "Left edges", right: "Right edges", top: "Top edges", bottom: "Bottom edges", center: "Centers (vertical)", middle: "Middles (horizontal)" };

export const LINE_HINT_TEXT = "Click to add points; double-click or Enter to finish; Esc to cancel.";
export const EDGE_PICK_HINT_TEXT = "Click a perimeter edge to align the grid to it. Esc cancels.";

function EditorToolbar({ editor, mode, selectedCount, onChange, padChoice = null, onPadChoice = () => {} }) {
  const snap = editor?.getSnapping();
  const set = (patch) => { editor?.setSnapping(patch); onChange(); };
  const off = !editor?.frame;
  const [lineType, setLineType] = useState(DEFAULT_LINE_TYPE);
  const draw = (type) => { editor?.startLine({ layer: type }); onChange(); };
  return (
    <div className="flex flex-wrap items-center justify-center gap-1 rounded-md border border-bg-border bg-bg/90 p-1 shadow" data-testid="pp-editor-toolbar">
      <button type="button" className={btn} aria-pressed={mode === "select"} onClick={() => editor?.cancel()}>Select</button>
      <button type="button" className={btn} aria-pressed={mode === "line"} disabled={off} onClick={() => draw(lineType)}
        title={LINE_HINT_TEXT} data-testid="pp-line-tool">Line</button>
      <select className="rounded border border-bg-border bg-bg px-1 py-1 text-[12.5px] text-fg-primary" aria-label="Line type" value={lineType}
        disabled={off} data-testid="pp-line-type"
        onChange={(e) => { setLineType(e.target.value); if (mode === "line") draw(e.target.value); }}>
        {LINE_TYPES.map((t) => <option key={t} value={t}>{LINE_TYPE_LABELS[t]}</option>)}
      </select>
      <span className="mx-1 h-5 w-px bg-bg-border" aria-hidden="true" />
      <details className="relative">
        <summary className={`${btn} list-none`} data-testid="pp-snap-menu">Snap {snap?.grid ? `${snap.gridFt} ft` : "off"}</summary>
        <div className="absolute left-0 top-9 z-10 flex w-56 flex-col gap-1.5 rounded-md border border-bg-border bg-bg p-2.5 text-[12.5px] text-fg-primary shadow-lg">
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!snap?.grid} onChange={(e) => set({ grid: e.target.checked })} /> Grid</label>
          <label className="flex items-center gap-2 pl-5">Step
            <select className="rounded border border-bg-border bg-bg px-1 py-0.5" value={snap?.gridFt ?? 5} onChange={(e) => set({ gridFt: Number(e.target.value) })} aria-label="Grid step">
              {[1, 5, 10, 25].map((v) => <option key={v} value={v}>{v} ft</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!snap?.resize} onChange={(e) => set({ resize: e.target.checked })} /> Resize in {snap?.resizeStepFt ?? 1} ft steps</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!snap?.rotation} onChange={(e) => set({ rotation: e.target.checked })} /> Rotation in {snap?.rotationStepDeg ?? 15}° steps</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!snap?.perimeter} onChange={(e) => set({ perimeter: e.target.checked })} /> Perimeter edges</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={!!snap?.lineEnds} onChange={(e) => set({ lineEnds: e.target.checked })} /> Line ends</label>
          <span className="my-0.5 h-px bg-bg-border" aria-hidden="true" />
          <label className="flex items-center gap-2" title="Turns the grid and the snap steps to the pad. Nothing saved changes.">
            <input type="checkbox" checked={!!padChoice} disabled={!editor?.perimeter} data-testid="pp-align-pad"
              onChange={(e) => onPadChoice(e.target.checked ? { mode: "long" } : null)} /> Align to pad
          </label>
          <button type="button" className="op-btn op-btn--sm self-start" disabled={!editor?.perimeter} data-testid="pp-align-edge"
            aria-pressed={mode === "edge"} onClick={() => { if (mode === "edge") editor?.cancel(); else editor?.startEdgePick(); onChange(); }}>
            Align to an edge…
          </button>
          {padChoice && <p className="op-meta" data-testid="pp-align-axis">{axisText(editor?.getPadAxis()) ?? "The picked edge is no longer on the perimeter."}</p>}
        </div>
      </details>
      <details className="relative">
        <summary className={`${btn} list-none`} aria-disabled={selectedCount < 2}>Align</summary>
        <div className="absolute left-0 top-9 z-10 flex w-48 flex-col gap-0.5 rounded-md border border-bg-border bg-bg p-1.5 shadow-lg">
          {selectedCount < 2 && <p className="op-meta px-1.5 py-1">Select two or more.</p>}
          {ALIGN_MODES.map((m) => (
            <button key={m} type="button" className="rounded px-1.5 py-1 text-left text-[12.5px] text-fg-primary hover:bg-bg-soft disabled:opacity-50"
              disabled={selectedCount < 2} onClick={() => { editor?.alignSelection(m); onChange(); }}>{ALIGN_LABELS[m]}</button>
          ))}
        </div>
      </details>
      <span className="mx-1 h-5 w-px bg-bg-border" aria-hidden="true" />
      <button type="button" className={btn} disabled={!editor?.canUndo} onClick={() => editor?.undo()}>Undo</button>
      <button type="button" className={btn} disabled={!editor?.canRedo} onClick={() => editor?.redo()}>Redo</button>
      <button type="button" className={btn} disabled={!selectedCount} onClick={() => editor?.duplicate()}>Duplicate</button>
      <button type="button" className={btn} disabled={!selectedCount} onClick={() => editor?.remove()}>Delete</button>
    </div>
  );
}

/* ── the Selected panel ────────────────────────────────────────────────── */

/** A number that commits on Enter or blur; an invalid entry shows the editor's refusal and changes nothing. */
function NumberField({ label, value, digits = 1, suffix, onCommit, testid }) {
  const shown = value === null || value === undefined ? "" : Number(value.toFixed(digits)).toString();
  const [draft, setDraft] = useState(null);
  const commit = () => {
    if (draft === null) return;
    const v = Number(draft);
    setDraft(null);
    if (draft.trim() !== "" && Number.isFinite(v) && v !== Number(shown)) onCommit(v);
  };
  return (
    <label className="flex flex-col gap-1">
      <span className="op-label uppercase">{label}</span>
      <span className="flex items-center gap-1">
        <input inputMode="decimal" className={field} value={draft ?? shown} data-testid={testid}
          onChange={(e) => setDraft(e.target.value)} onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") setDraft(null); }} />
        {suffix && <span className="op-meta">{suffix}</span>}
      </span>
    </label>
  );
}

function TextField({ label, value, onCommit, list, testid }) {
  const [draft, setDraft] = useState(null);
  const commit = () => { if (draft !== null && draft !== (value ?? "")) onCommit(draft); setDraft(null); };
  return (
    <label className="flex flex-col gap-1">
      <span className="op-label uppercase">{label}</span>
      <input className={field} value={draft ?? value ?? ""} list={list} data-testid={testid}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") setDraft(null); }} />
    </label>
  );
}

function SelectedPanel({ editor, features, templatesById, wells, onChange, onWells }) {
  const [error, setError] = useState(null);
  const [latLng, setLatLng] = useState(false);
  const f = features.length === 1 ? features[0] : null;
  const apply = (fields) => {
    const r = editor.setFields(f.id, fields);
    setError(r.ok ? null : r.message);
    onChange();
  };

  if (!f) {
    return (
      <div className="flex flex-col gap-2 rounded-md border border-bg-border bg-bg/95 p-3 shadow-lg" data-testid="pp-selected">
        <div className="op-label uppercase">Selected</div>
        <p className="text-[13px] text-fg-primary">{features.length} selected</p>
        <p className="op-meta">Align from the toolbar; Duplicate and Delete apply to all of them.</p>
      </div>
    );
  }

  const t = f.template_id ? templatesById.get(f.template_id) : null;
  const frame = editor.frame;
  const dims = f.kind === "footprint" ? footprintDims(f, t) : null;
  const en = f.anchor ? frame.toLocal(f.anchor.coordinates) : null;
  const layers = [...new Set([...editor.layers(), ...[...templatesById.values()].map((x) => x.default_layer).filter(Boolean)])];
  const takenBy = new Map(editor.getFeatures().filter((g) => g.well_api10 && g.id !== f.id).map((g) => [g.well_api10, g.id]));
  const name = t?.name ?? (f.kind === "line" ? lineTypeLabel(f.layer) : f.kind === "zone" ? "Zone" : "Footprint");
  const lengths = f.kind === "line" && frame ? lineLengthsFt(f.line_geom?.coordinates, frame) : null;
  const vertex = f.kind === "line" ? editor.getSelectedVertex() : null;

  return (
    <div className="flex flex-col gap-2.5 rounded-md border border-bg-border bg-bg/95 p-3 shadow-lg" data-testid="pp-selected">
      <div className="op-label uppercase">Selected</div>
      <div>
        <p className="text-[14px] font-semibold text-fg-primary" data-testid="pp-selected-name">{name}</p>
        <p className="op-meta">{t ? `${CATEGORY_LABELS[t.category] ?? t.category ?? ""}` : f.kind === "line" ? "Line" : "Freeform"}</p>
      </div>
      {lengths && (
        <div className="flex flex-col gap-2" data-testid="pp-line">
          <p className="text-[13px] text-fg-primary" data-testid="pp-line-length">
            {formatFeet(lengths.total)} <span className="op-meta">· {lengths.segments.length} segment{lengths.segments.length === 1 ? "" : "s"}</span>
          </p>
          <label className="flex flex-col gap-1">
            <span className="op-label uppercase">Type</span>
            <select className={field} value={f.layer} aria-label="Line type" data-testid="pp-line-layer" onChange={(e) => apply({ layer: e.target.value })}>
              {!LINE_TYPES.includes(f.layer) && <option value={f.layer}>{f.layer}</option>}
              {LINE_TYPES.map((x) => <option key={x} value={x}>{LINE_TYPE_LABELS[x]}</option>)}
            </select>
          </label>
          <div className="flex items-end gap-2">
            <NumberField label="Disturbance width" suffix="ft" digits={1} value={lineWidthFt(f)} testid="pp-line-width" onCommit={(v) => apply({ line_width_ft: v })} />
            {lineWidthFt(f) !== null && (
              <button type="button" className="op-btn op-btn--sm mb-0.5" onClick={() => apply({ line_width_ft: null })}>Clear</button>
            )}
          </div>
          <p className="op-meta">Optional. Distances are measured to the centerline.</p>
          <p className="op-meta" data-testid="pp-line-vertex-hint">
            Drag a point to move it, drag or click a midpoint to add one, Alt-click a point to remove it.
          </p>
          {vertex && (
            <button type="button" className="op-btn op-btn--sm self-start" data-testid="pp-delete-vertex"
              disabled={(f.line_geom?.coordinates?.length ?? 0) <= 2}
              onClick={() => { const r = editor.deleteLineVertex(f.id, vertex.index); setError(r.ok ? null : r.message); onChange(); }}>
              Delete point {vertex.index + 1}
            </button>
          )}
        </div>
      )}
      {f.kind === "footprint" && dims && (
        <div className="grid grid-cols-2 gap-2">
          <NumberField label="Length" suffix="ft" digits={2} value={dims.lengthFt} testid="pp-length" onCommit={(v) => apply({ length_ft: v })} />
          <NumberField label="Width" suffix="ft" digits={2} value={dims.widthFt} testid="pp-width" onCommit={(v) => apply({ width_ft: v })} />
          <NumberField label="Rotation" suffix="°" digits={1} value={f.rotation_deg} testid="pp-rotation" onCommit={(v) => apply({ rotation_deg: v })} />
          {editor.getPadAxis() && (
            <p className="op-meta col-span-2" data-testid="pp-rotation-relative">
              {(f.rotation_deg ?? 0).toFixed(1)}° from grid north · {relativeDeg(f.rotation_deg, editor.getPadAxis()).toFixed(1)}° relative to pad
            </p>
          )}
        </div>
      )}
      {f.kind === "zone" && (
        <NumberField label="Radius" suffix="ft" digits={1} value={zoneRadiusFt(f, t)} testid="pp-radius" onCommit={(v) => apply({ radius_ft: v })} />
      )}
      {en && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="op-label uppercase" data-testid="pp-position-label">Position · {latLng ? "lat, lng" : utmZoneLabel(frame.epsg)}</span>
            <button type="button" className="op-meta hover:text-fg-primary" onClick={() => setLatLng((v) => !v)}>{latLng ? "UTM" : "Lat, lng"}</button>
          </div>
          {latLng ? (
            <div className="grid grid-cols-2 gap-2">
              <NumberField label="Lat" digits={6} value={f.anchor.coordinates[1]} testid="pp-lat" onCommit={(v) => apply({ position: { lat: v, lng: f.anchor.coordinates[0] } })} />
              <NumberField label="Lng" digits={6} value={f.anchor.coordinates[0]} testid="pp-lng" onCommit={(v) => apply({ position: { lat: f.anchor.coordinates[1], lng: v } })} />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <NumberField label="E" suffix="m" digits={1} value={en[0]} testid="pp-east" onCommit={(v) => apply({ position: { e: v, n: en[1] } })} />
              <NumberField label="N" suffix="m" digits={1} value={en[1]} testid="pp-north" onCommit={(v) => apply({ position: { e: en[0], n: v } })} />
            </div>
          )}
        </div>
      )}
      {f.kind !== "line" && (
        <>
          <TextField label="Layer" value={f.layer} list="pp-layer-options" testid="pp-layer" onCommit={(v) => apply({ layer: v })} />
          <datalist id="pp-layer-options">{layers.map((l) => <option key={l} value={l} />)}</datalist>
        </>
      )}
      <TextField label="Label" value={f.label} testid="pp-label" onCommit={(v) => apply({ label: v })} />
      {(f.kind === "footprint" || f.kind === "zone") && (
        <div className="flex flex-col gap-1" data-testid="pp-wellhead">
          <span className="op-label uppercase">Wellhead</span>
          {wells.length === 0 ? (
            <p className="op-meta">
              {NO_WELLS_TEXT}{" "}
              <button type="button" className="text-accent-action hover:underline" onClick={onWells}>Add wells on the plan page</button>
            </p>
          ) : (
            <select className={field} value={f.well_api10 ?? ""} aria-label="Wellhead"
              onChange={(e) => apply({ well_api10: e.target.value || null })}>
              <option value="">None</option>
              {wells.map((w) => (
                <option key={w.api10} value={w.api10} disabled={takenBy.has(w.api10)}>
                  {w.label ? `${w.label} · ` : ""}{formatApi10(w.api10)}{takenBy.has(w.api10) ? " (marked)" : ""}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
      {error && <p className="text-[12.5px] text-accent-red" role="alert">{error}</p>}
    </div>
  );
}
